import { Trophy, Globe, User, BarChart3, RefreshCw } from 'lucide-react';
import { useAppBoard } from '../hooks/useAppBoard';
import { LoadingSpinner } from './LoadingSpinner';
import { useState } from 'react';
import { AppAssetsModal } from './modals/AppAssetsModal';

export const AppBoard = () => {
  const { data, loading, error, refetch } = useAppBoard();
  const [selectedApp, setSelectedApp] = useState<{ id: string; name: string; count: number } | null>(null);

  const getTrophyColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-status-warning';
      case 2:
        return 'text-th-secondary';
      case 3:
        return 'text-accent';
      default:
        return 'text-th-secondary';
    }
  };

  const getRankDisplay = (rank: number) => {
    if (rank <= 3) {
      return <Trophy className={`w-5 h-5 ${getTrophyColor(rank)}`} />;
    }
    return <span className="text-th-secondary font-medium">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-th-secondary">Loading app board...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4 max-w-md">
          <BarChart3 className="w-16 h-16 text-status-error mx-auto" />
          <h3 className="text-xl font-bold text-th-primary">Failed to Load App Board</h3>
          <p className="text-th-secondary">{error}</p>
          <button onClick={refetch} className="button-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4">
          <BarChart3 className="w-16 h-16 text-th-secondary mx-auto" />
          <h3 className="text-xl font-bold text-th-primary">No App Data</h3>
          <p className="text-th-secondary">
            No AI-generated assets found yet. Apps will appear here once users start generating content.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-brand rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-th-primary">App Board</h2>
              <p className="text-th-secondary">Application rankings by AI-generated content</p>
            </div>
          </div>
          <button
            onClick={refetch}
            className="button-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <h4 className="text-th-secondary text-xs font-medium tracking-wide uppercase mb-1">Total Apps</h4>
          <p className="text-2xl font-bold text-brand">{data.length}</p>
        </div>
        <div className="card p-4">
          <h4 className="text-th-secondary text-xs font-medium tracking-wide uppercase mb-1">Total AI Assets</h4>
          <p className="text-2xl font-bold text-accent">
            {data.reduce((sum, app) => sum + app.counts, 0).toLocaleString()}
          </p>
        </div>
        <div className="card p-4">
          <h4 className="text-th-secondary text-xs font-medium tracking-wide uppercase mb-1">Most Active App</h4>
          <p className="text-2xl font-bold text-status-warning">{data[0]?.app_name || 'None'}</p>
        </div>
      </div>

      <div className="card p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-subtle">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Rank</th>
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">App Name</th>
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">App ID</th>
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">AI Assets</th>
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Most Active User</th>
                <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Country</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-subtle">
              {data.map((app, index) => {
                const rank = index + 1;

                return (
                  <tr
                    key={app.app_id}
                    className="hover:bg-overlay transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center">
                        {getRankDisplay(rank)}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-brand rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">
                            {app.app_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-th-primary font-medium">{app.app_name}</p>
                          {app.route_paths.length > 1 && (
                            <p className="text-th-secondary text-xs">
                              +{app.route_paths.length - 1} more pages
                            </p>
                          )}
                          {rank <= 3 && (
                            <p className="text-xs text-status-warning">
                              {rank === 1 ? 'Top Performer' : rank === 2 ? 'Runner Up' : 'Third Place'}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <code className="text-th-primary font-mono text-xs bg-surface px-2 py-1 rounded-lg">
                        {app.app_id}
                      </code>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-th-primary font-semibold text-lg">
                          {app.counts.toLocaleString()}
                        </span>
                        <button
                          onClick={() => setSelectedApp({
                            id: app.app_id,
                            name: app.app_name,
                            count: app.counts
                          })}
                          className="text-xs px-2 py-1 bg-brand/20 text-brand rounded-full hover:bg-brand/30 transition-all cursor-pointer"
                          title={`View all ${app.counts} AI assets for ${app.app_name}`}
                        >
                          AI Assets
                        </button>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-accent" />
                        </div>
                        <div>
                          <p className="text-th-primary font-medium text-sm">
                            {app.most_used_by_name || 'Unknown User'}
                          </p>
                          <p className="text-th-secondary text-xs font-mono">
                            {app.most_used_by_id ? `${app.most_used_by_id.slice(0, 8)}...` : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-status-warning" />
                        <span className="text-th-primary font-medium">
                          {app.most_used_in_country || 'Unknown'}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 pt-4 border-t border-subtle text-center">
          <p className="text-th-secondary text-sm">
            Showing {data.length} app{data.length !== 1 ? 's' : ''} ranked by AI-generated content
          </p>
        </div>
      </div>

      {selectedApp && (
        <AppAssetsModal
          isOpen={!!selectedApp}
          onClose={() => setSelectedApp(null)}
          appId={selectedApp.id}
          appName={selectedApp.name}
          assetCount={selectedApp.count}
        />
      )}
    </div>
  );
};
